// Shared SSE utilities (website)
// Provides SSEManager and factory for use by client src/hooks/useSSE.ts

import {
  SseManagerCore,
  type SseCoreConfig,
  type SseCoreConnection,
  type SseCoreHandlers,
} from "./sseManagerCore";

// Generate a unique connection ID for better server-side tracking
// Works across multiple browser tabs, windows, and sessions
export function generateConnectionId(): string {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).slice(2, 9);
  // Use window.location.origin to differentiate between different domains/ports
  const origin =
    typeof window !== "undefined" ? window.location.origin : "unknown";
  const originHash = origin
    .split("")
    .reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0)
    .toString(36)
    .slice(-4);
  return `web-${originHash}-${timestamp}-${randomId}`;
}

export interface SSEConfig extends SseCoreConfig {}

export interface SSEMessage {
  type: string;
  content?: string;
  message_id?: string;
  ai_thread_id?: string;
  user_id?: string;
  created_at?: any;
  isStreaming?: boolean;
  update?: string;
  tool_name?: any;
  tool_description?: any;
  args_json?: any;
  steps_taken?: any[];
  arg_types_json?: any;
  human_review?: any;
  thread_name?: string;
  plan_name?: any;
  plan_description?: any;
  plan_steps?: any;
  plan_id?: any;
  plan_url?: any;
  information_request?: any;
  plan_progress?: {
    plan_description?: string;
    completed_step_indices: number[];
    total_steps: number;
    messages?: string[];
  };
  [key: string]: unknown;
}

export interface SSEHandlers extends SseCoreHandlers<SSEMessage> {}
export interface SSEConnection extends SseCoreConnection {}

export class SSEManager {
  private core: SseManagerCore<SSEMessage>;

  constructor(config: SSEConfig, handlers: SSEHandlers) {
    this.core = new SseManagerCore<SSEMessage>(config, handlers, {
      createConnectionId: ({ reconnectAttempts, configConnectionId }) => {
        if (configConnectionId && reconnectAttempts === 0) {
          return configConnectionId;
        }
        return generateConnectionId();
      },
      preserveConfigConnectionIdOnFirstConnect: true,
      aiChunkMessageType: "ai_chunk",
      touchHeartbeatOnEvents: false,
      requireCurrentEventSource: true,
      blockConnectWhileEventSourceConnecting: true,
      enforceLock: false,
      pingMismatchState: "disconnected",
      heartbeatFailureState: "disconnected",
      errorState: "disconnected",
      callOnDisconnectedOnPingMismatch: false,
      callOnDisconnectedOnHeartbeatFailure: true,
      callOnDisconnectedOnError: true,
      callOnDisconnectedOnMaxReconnect: false,
      includeMessageProcessedEvent: true,
      includeMessageErrorEvent: true,
      humanReviewRequestMode: "message",
      initialLastHeartbeat: Date.now(),
    });
  }

  public connect(): void {
    this.core.connect();
  }

  public disconnect(): void {
    this.core.disconnect();
  }

  public getConnectionState(): SSEConnection {
    return this.core.getConnectionState();
  }

  public isConnected(): boolean {
    return this.core.isConnected();
  }

  public getConnectionStatus():
    | "disconnected"
    | "connecting"
    | "connected"
    | "reconnecting" {
    return this.core.getConnectionStatus();
  }

  public updateHandlers(handlers: SSEHandlers): void {
    this.core.updateHandlers(handlers);
  }
}

export function createSSEManager(
  config: SSEConfig,
  handlers: SSEHandlers,
): SSEManager {
  return new SSEManager(config, handlers);
}
